import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { RefreshCw } from 'lucide-react';

import { getSignInHelperLinks } from '../config/signInHelperLinks';
import type { SignInHelperState } from '../hooks/useSignInHelper';
import type { DeviceCodeBlockState } from '../lib/deviceCodeBlocking';
import { deviceCodeLikelyBlocked } from '../lib/deviceCodeBlocking';
import type { SignInHelperLinks } from '../config/consoleConfig';
import { IDENTIFIER, TEXT } from '../theme/tokens';

import CopyValueButton from './CopyValueButton';
import { HelperInstallLinks, ManualInstallSteps } from './SignInHelperInstall';
import { DeviceCodeBlockNotice, ReloadedSignInNotice } from './SignInPathNotices';

// FD-592 (FD-ADR-047 §5) — the Sign-in helper check on the Activate page.
//
// ALWAYS SHOWN, because the browser sign-in leads (owner decision at FD-ADR-047's
// acceptance) and the helper is what makes it automatic. MORE INSISTENT when the
// named probe says a policy blocks device code, because then the browser sign-in
// is not just the first option — it is the way in.
//
// IT GATES NOTHING. Like every check on this page, it informs; `activate-wave`
// refuses on its own terms (FD-296). Without the helper the browser sign-in still
// works with one paste. A test asserts the Activate button is enabled whether the
// helper is present, absent, or still being checked.
//
// Store links come from runtime config (`config.json` → `signInHelper`). The store
// listings get their ids at first upload and a Self-hosted console may have its own
// install route, so absent links say "from your administrator" rather than render a
// link that goes nowhere.
//
// FD-608 — the INTERIM channel. Until the store listings exist (review takes up to two
// weeks, FD-605) a console host serves the helper itself: a zip for a manual
// "Load unpacked" install, and a signed CRX + update.xml for IT to force-install.
// Store links win when present; the download then stays as a secondary link. The
// steps below are the browser's, stated plainly, including that a Developer-mode
// install is interim and may draw a browser warning.

export default function SignInHelperCheck({
  helper,
  blockState,
}: {
  helper: SignInHelperState;
  blockState: DeviceCodeBlockState;
}) {
  const links = getSignInHelperLinks();
  const blocked = deviceCodeLikelyBlocked(blockState);
  const { status } = helper;

  const badge =
    status === 'checking' ? (
      <Badge appearance="tint" color="subtle">
        Checking
      </Badge>
    ) : status === 'present' ? (
      <Badge appearance="tint" color="success">
        Installed
      </Badge>
    ) : (
      <Badge appearance="tint" color={blocked ? 'warning' : 'subtle'}>
        Not detected
      </Badge>
    );

  const hasStore = Boolean(links?.edgeStoreUrl || links?.chromeStoreUrl);
  const downloadUrl = links?.downloadUrl;
  const installLinks = <HelperInstallLinks links={links} />;

  // The manual steps are shown open when the download is the primary route, and behind
  // a disclosure when a store link leads (the download is then a fallback).
  const showSteps = status !== 'present' && Boolean(downloadUrl);
  const forcelist =
    links?.extensionId && links?.forceInstallUpdateUrl
      ? `${links.extensionId};${links.forceInstallUpdateUrl}`
      : undefined;

  return (
    <div className="flex flex-col gap-3" data-testid="sign-in-helper-check">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-[13px] font-medium" style={{ color: TEXT.primary }}>
          Sign-in helper {badge}
        </span>
        <Button
          appearance="subtle"
          size="small"
          icon={<RefreshCw size={14} strokeWidth={1.5} />}
          onClick={helper.recheck}
        >
          Check again
        </Button>
      </div>

      {helper.orphanResult && <ReloadedSignInNotice />}

      {status === 'present' ? (
        <p className="m-0 text-[12.5px] leading-snug" style={{ color: TEXT.secondary }}>
          After the browser sign-in, the helper brings the sign-in back to this page and the console
          continues by itself.
        </p>
      ) : blocked ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Install the sign-in helper before you activate</MessageBarTitle>
            Device code is blocked for the minting account, so the browser sign-in is the way in.
            Without the helper it still works: you paste the address the sign-in tab lands on. With
            it, the console continues by itself. {installLinks}
          </MessageBarBody>
        </MessageBar>
      ) : (
        <p className="m-0 text-[12.5px] leading-snug" style={{ color: TEXT.secondary }}>
          {status === 'checking' ? 'Looking for the AirBridge sign-in helper… ' : 'Not detected in this browser. '}
          The browser sign-in works without it — you paste one address back. The helper removes that
          step. If it is installed but this console is not one it trusts, open the helper on this tab
          and allow this console. {installLinks}
        </p>
      )}

      {showSteps && !hasStore && <ManualInstallSteps />}

      {((showSteps && hasStore) || forcelist) && (
        <Accordion collapsible multiple>
          {showSteps && hasStore && (
            <AccordionItem value="manual">
              <AccordionHeader size="small">Install the downloaded build</AccordionHeader>
              <AccordionPanel>
                <ManualInstallSteps />
              </AccordionPanel>
            </AccordionItem>
          )}
          {forcelist && links && (
            <AccordionItem value="it">
              <AccordionHeader size="small">For IT: force-install by policy</AccordionHeader>
              <AccordionPanel>
                <ForceInstallSnippet forcelist={forcelist} links={links} />
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      )}

      <DeviceCodeBlockNotice state={blockState} prominent={false} />

      <p className="m-0 text-[11.5px]" style={{ color: TEXT.secondary }}>
        Advisory. The helper never affects whether you can activate.
      </p>
    </div>
  );
}

function ForceInstallSnippet({ forcelist, links }: { forcelist: string; links: SignInHelperLinks }) {
  return (
    <div className="flex flex-col gap-2 text-[12.5px] leading-snug" style={{ color: TEXT.secondary }} data-testid="helper-forcelist">
      <p className="m-0">
        Add this value to the <span style={IDENTIFIER}>ExtensionInstallForcelist</span> policy for
        Microsoft Edge or Google Chrome (Intune settings catalog or Group Policy). Off-store
        force-install works only on managed devices: domain-joined or Entra hybrid-joined on
        Windows, MDM-managed on macOS.
      </p>
      <div className="flex items-center gap-1">
        <code className="break-all" style={{ ...IDENTIFIER, color: TEXT.primary }} data-testid="helper-forcelist-value">
          {forcelist}
        </code>
        <CopyValueButton value={forcelist} label="the forcelist value" />
      </div>
      <p className="m-0">
        Extension ID <span style={IDENTIFIER}>{links.extensionId}</span> is the interim, self-hosted
        build. The store listings will have their own IDs.
      </p>
    </div>
  );
}
